import { Highlight, type PrismTheme, themes } from 'prism-react-renderer';
import { useEffect, useState } from 'react';
import Editor from 'react-simple-code-editor';
import { cn } from '../lib/cn';

/** 与 admin 其余部分共用的等宽排版。必须同时喂给 textarea 与高亮层 ——
 *  react-simple-code-editor 把透明 textarea 叠在高亮 pre 上,两层字距一旦不同
 *  光标就会跟字符错位。 */
const EDITOR_TYPOGRAPHY = {
  fontFamily:
    'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
  fontSize: 12,
  lineHeight: 1.7,
} as const;

const DARK_QUERY = '(prefers-color-scheme: dark)';

/**
 * admin 的暗色跟随系统偏好(styles.css 里没有 .dark class),所以主题只能在 JS 里选。
 *
 * 这里刻意不给 `window.matchMedia` 加存在性降级:admin 是纯 CSR 的 vite SPA,
 * 浏览器里它必然存在。缺它的只有 jsdom(实测 `typeof window.matchMedia === 'undefined'`)——
 * 那是测试环境的空缺,要为本组件写测试就在 setup 里 `vi.stubGlobal('matchMedia', ...)`,
 * 别在这里加一条永远不会在生产执行、也无从测试的分支。
 */
function usePrefersDark(): boolean {
  const [dark, setDark] = useState(() => window.matchMedia(DARK_QUERY).matches);
  useEffect(() => {
    const mq = window.matchMedia(DARK_QUERY);
    const onChange = (e: MediaQueryListEvent) => setDark(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return dark;
}

/** 主题自带背景色,这里剥掉 —— 背景交给外层容器,才能和 Input 的边框/焦点环一致。
 *  两份主题在模块级备好:编辑器每敲一个键都会 re-render,每次新建 theme 对象会让
 *  Highlight 拿到不同引用而白白失效。 */
const transparentBg = (theme: PrismTheme): PrismTheme => ({
  ...theme,
  plain: { ...theme.plain, backgroundColor: 'transparent' },
});
const DARK_THEME = transparentBg(themes.vsDark);
const LIGHT_THEME = transparentBg(themes.vsLight);

export interface JsonEditorProps {
  value: string;
  onChange: (next: string) => void;
  /** 校验失败时描红边框,让错误在滚动到别处时也有痕迹。 */
  invalid?: boolean;
  className?: string;
}

/**
 * 带 JSON 语法高亮的编辑框。底层就是一个 textarea,所以粘贴、全选、撤销、
 * 输入法全是浏览器原生行为 —— 换掉的只是「看不清」。
 */
export function JsonEditor({ value, onChange, invalid, className }: JsonEditorProps) {
  const theme = usePrefersDark() ? DARK_THEME : LIGHT_THEME;

  return (
    <div
      className={cn(
        // resize-y 保留原生 textarea 时就有的拖拽调高 —— 一百多行配置常要拉大来看。
        'overflow-auto rounded-md border bg-transparent shadow-xs',
        'resize-y focus-within:ring-[3px]',
        invalid
          ? 'border-red-500/60 focus-within:border-red-500 focus-within:ring-red-500/30'
          : 'border-input focus-within:border-ring focus-within:ring-ring/50',
        className,
      )}
    >
      <Editor
        value={value}
        onValueChange={onChange}
        padding={12}
        textareaClassName="outline-none"
        spellCheck={false}
        highlight={(code) => (
          <Highlight code={code} language="json" theme={theme}>
            {({ tokens, getLineProps, getTokenProps }) =>
              tokens.map((line, i) => (
                // 行内容可重复(成片的 "true"),行号才是稳定身份;这些行不会被
                // 重排或增删单行,用 index 做 key 是安全的。
                // biome-ignore lint/suspicious/noArrayIndexKey: 行号即身份
                <div key={i} {...getLineProps({ line })}>
                  {line.map((token, key) => (
                    // biome-ignore lint/suspicious/noArrayIndexKey: 同上,列位置即身份
                    <span key={key} {...getTokenProps({ token })} />
                  ))}
                </div>
              ))
            }
          </Highlight>
        )}
        style={EDITOR_TYPOGRAPHY}
      />
    </div>
  );
}
